import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChecklistVersionDto, ConfigDto, IntegrityCheckDto, InviteBulkDto, InviteDto, LogDto, MailOutboxDto, MailOutboxPageDto, NotificationDto, NotificationPreviewDto, NotificationsConfig, NotificationsConfigDto, PolicyAcceptanceDto, PolicyVersionDto, QuestionDto, RetentionRunDto, Role, UserDto } from "@audit-plan/shared";
import { api } from "./client";

const list = <T,>(r: T[] | { items: T[] }) => (Array.isArray(r) ? r : r.items);

export const useUsers = () => useQuery({ queryKey: ["users"], queryFn: async () => list(await api.get<UserDto[] | { items: UserDto[] }>("/users")) });

export function useUserMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["users"] });
    void qc.invalidateQueries({ queryKey: ["people"] });
  };
  return {
    invite: useMutation({
      mutationFn: (b: { email: string; name: string; role: Role; supplierId?: string | null }) => api.post<InviteDto>("/users/invite", b),
      onSuccess: done,
    }),
    patch: useMutation({
      mutationFn: ({ id, body }: { id: string; body: { name?: string; role?: Role; supplierId?: string | null; disabled?: boolean } }) => api.patch<UserDto>(`/users/${id}`, body),
      onSuccess: done,
    }),
    reinvite: useMutation({ mutationFn: (id: string) => api.post<InviteDto>(`/users/${id}/reinvite`), onSuccess: done }),
    resetMfa: useMutation({ mutationFn: (id: string) => api.post<UserDto>(`/users/${id}/reset-mfa`), onSuccess: done }),
    inviteBulk: useMutation({
      mutationFn: (b: { supplierIds?: string[]; dryRun: boolean }) => api.post<InviteBulkDto>("/users/invite-bulk", b),
      onSuccess: (r) => {
        if (!r.dryRun) {
          done();
          void qc.invalidateQueries({ queryKey: ["mail-outbox"] });
        }
      },
    }),
  };
}

export const useChecklists = () => useQuery({ queryKey: ["checklists"], queryFn: async () => list(await api.get<ChecklistVersionDto[] | { items: ChecklistVersionDto[] }>("/checklists")) });

export const useQuestions = (versionId?: string) =>
  useQuery({
    queryKey: ["questions", versionId],
    queryFn: async () => list(await api.get<QuestionDto[] | { items: QuestionDto[] }>(`/checklists/${versionId}/questions`)),
    enabled: !!versionId,
  });

export interface QuestionInput {
  section: string;
  number: string;
  area: string;
  it: string;
  en: string;
  points: number;
  order: number;
  active: boolean;
  zeroTolerance?: boolean;
}
export function useChecklistMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["questions"] });
    void qc.invalidateQueries({ queryKey: ["checklists"] });
  };
  return {
    create: useMutation({ mutationFn: ({ versionId, body }: { versionId: string; body: QuestionInput }) => api.post<QuestionDto>(`/checklists/${versionId}/questions`, body), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<QuestionInput> }) => api.patch<QuestionDto>(`/questions/${id}`, body), onSuccess: done }),
    clone: useMutation({ mutationFn: ({ versionId, code, title }: { versionId: string; code: string; title: string }) => api.post<ChecklistVersionDto>(`/checklists/${versionId}/clone`, { code, title }), onSuccess: done }),
  };
}

export function useSaveConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: ConfigDto) => api.put<ConfigDto>("/config", b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["config"] });
      void qc.invalidateQueries({ queryKey: ["plan"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** Registro modifiche con cursore: carica un'altra pagina con before = ts dell'ultima riga. */
export const useNotifications = () =>
  useQuery({ queryKey: ["notifications"], queryFn: async () => list(await api.get<NotificationDto[] | { items: NotificationDto[] }>("/notifications")) });

/** Configurazione di quando e come inviare (admin). */
export const useNotifConfig = () => useQuery({ queryKey: ["notif-config"], queryFn: () => api.get<NotificationsConfigDto>("/notifications/config"), refetchInterval: 30_000 });
export function useSaveNotifConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: NotificationsConfig) => api.put<NotificationsConfigDto>("/notifications/config", b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notif-config"] });
      void qc.invalidateQueries({ queryKey: ["config"] });
      void qc.invalidateQueries({ queryKey: ["log"] });
    },
  });
}
export const useTestNotification = () =>
  useMutation({
    mutationFn: (kind: string) => api.post<{ queued: boolean; to: string }>("/notifications/test", { kind }),
  });
export const useNotifPreview = (kind: string | null, lang: "it" | "en") =>
  useQuery({ queryKey: ["notif-preview", kind, lang], enabled: !!kind, queryFn: () => api.get<NotificationPreviewDto>("/notifications/preview", { kind: kind!, lang }) });

export function useRunScheduler() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<unknown>("/scheduler/run"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notifications"] });
      void qc.invalidateQueries({ queryKey: ["log"] });
    },
  });
}

export const fetchLog = async (q: { entity?: string; before?: string; limit?: number }) => list(await api.get<LogDto[] | { items: LogDto[] }>("/log", q));

export const seedReset = () => api.post<unknown>("/admin/seed-reset");

/** Posta in uscita (admin): elenco con conteggi per stato, provider attivo e domini ammessi. */
export const useMailOutbox = (status?: string) =>
  useQuery({
    queryKey: ["mail-outbox", status ?? "all"],
    queryFn: () => api.get<MailOutboxPageDto>("/mail/outbox", { status, limit: 200 }),
    refetchInterval: 15_000,
  });

export function useRetryMail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<MailOutboxDto>(`/mail/outbox/${id}/retry`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mail-outbox"] }),
  });
}

/* ---------------- informative GDPR, conservazione, integrità ---------------- */
export const usePolicies = () => useQuery({ queryKey: ["policies"], queryFn: () => api.get<{ items: PolicyVersionDto[] }>("/policies").then((r) => r.items) });
export const usePolicyAcceptances = (policyVersionId?: string) =>
  useQuery({ queryKey: ["policy-acceptances", policyVersionId ?? "all"], queryFn: () => api.get<{ items: PolicyAcceptanceDto[] }>("/policies/acceptances", { policyVersionId, limit: 500 }).then((r) => r.items) });
export function usePolicyMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["policies"] });
    void qc.invalidateQueries({ queryKey: ["policy-acceptances"] });
  };
  return {
    create: useMutation({ mutationFn: (b: { code: string; title: string; body: string; audience: "supplier" | "internal" }) => api.post<PolicyVersionDto>("/policies", b), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<{ code: string; title: string; body: string; audience: "supplier" | "internal" }> }) => api.patch<PolicyVersionDto>(`/policies/${id}`, body), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: string) => api.del<void>(`/policies/${id}`), onSuccess: done }),
    publish: useMutation({ mutationFn: (id: string) => api.post<PolicyVersionDto>(`/policies/${id}/publish`), onSuccess: done }),
  };
}
export const useRetentionReport = () => useQuery({ queryKey: ["retention"], queryFn: () => api.get<RetentionRunDto | null>("/retention/report") });
export function useRunRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RetentionRunDto>("/retention/run"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["retention"] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
export const useIntegrityChecks = () => useQuery({ queryKey: ["integrity"], queryFn: () => api.get<{ items: IntegrityCheckDto[] }>("/integrity/checks").then((r) => r.items) });
export function useVerifyIntegrity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { sample?: number; since?: "start" | "checkpoint" }) => api.post<IntegrityCheckDto>("/integrity/verify", b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["integrity"] }),
  });
}
